/**
 * UserEmailPicker — email-search autocomplete for admin flows
 * ============================================================
 * Wraps `GET /admin/users?search=<q>&per_page=8` with a 250ms debounce.
 * Renders a dropdown of matches (email · name · aff_id · user_id). On pick,
 * calls `onPick({ user_id, email, name, aff_id })` and locks the input to
 * show the resolved user with a small "Clear" chip.
 *
 * Used from /admin/users/{id} > Affiliate Information > Sponsor edit slot.
 */
import { useEffect, useRef, useState } from "react";
import { authAxios } from "../../utils/auth";
import { Input } from "../ui/input";
import { Loader2, X, User as UserIcon } from "lucide-react";

export const UserEmailPicker = ({
  value,               // { user_id, email, name, aff_id } | null
  onPick,              // (user|null) => void
  excludeUserId,       // optional user_id to filter out of results (self)
  placeholder = "Search by email or name…",
  autoFocus = false,
  testIdPrefix = "user-email-picker",
}) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Debounced search
  useEffect(() => {
    if (value) return;          // already picked — don't re-search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setResults([]); setLoading(false); return;
    }
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      setLoading(true); setError(null);
      try {
        const { data } = await authAxios.get("/admin/users", {
          params: { search: q.trim(), per_page: 8, page: 1 },
          signal: abortRef.current.signal,
        });
        const filtered = (data.users || []).filter(
          (u) => !excludeUserId || u.user_id !== excludeUserId
        );
        setResults(filtered);
        setOpen(true);
      } catch (e) {
        if (e.name !== "CanceledError" && e.name !== "AbortError") {
          setError("Search failed");
        }
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, value, excludeUserId]);

  const pick = (u) => {
    onPick({
      user_id: u.user_id,
      email: u.email,
      name: u.name,
      aff_id: u.aff_id,
    });
    setQ(""); setResults([]); setOpen(false);
  };

  const clear = () => {
    onPick(null);
    setQ(""); setResults([]); setOpen(false);
  };

  // Picked state — show a locked chip
  if (value) {
    return (
      <div
        data-testid={`${testIdPrefix}-picked`}
        className="flex items-center justify-between gap-2 bg-divine-black border border-divine-gold/30 rounded-md px-3 py-2"
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white truncate flex items-center gap-1.5">
            <UserIcon className="h-3.5 w-3.5 text-divine-gold shrink-0" />
            <span className="truncate">{value.email}</span>
          </div>
          <div className="text-xs text-gray-500 truncate mt-0.5">
            {value.name || "—"} · <span className="font-mono">{value.user_id}</span>
            {value.aff_id ? <> · aff <span className="font-mono">{value.aff_id}</span></> : null}
          </div>
        </div>
        <button
          type="button"
          onClick={clear}
          data-testid={`${testIdPrefix}-clear`}
          className="text-gray-400 hover:text-red-400 shrink-0 rounded p-1 hover:bg-red-500/10"
          title="Clear selection"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // Search state
  return (
    <div ref={wrapRef} className="relative">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="bg-divine-black border-divine-gold/30 text-white text-sm pr-8"
        data-testid={`${testIdPrefix}-input`}
      />
      {loading && (
        <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
      )}
      {open && (results.length > 0 || (!loading && q.trim().length >= 2)) && (
        <div
          data-testid={`${testIdPrefix}-dropdown`}
          className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-divine-charcoal border border-divine-gold/30 rounded-md shadow-lg"
        >
          {results.length === 0 && !loading ? (
            <div className="px-3 py-2 text-xs text-gray-500">
              No users match "{q.trim()}"
            </div>
          ) : (
            results.map((u) => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => pick(u)}
                data-testid={`${testIdPrefix}-option-${u.user_id}`}
                className="w-full text-left px-3 py-2 hover:bg-divine-gold/10 border-b border-white/5 last:border-b-0"
              >
                <div className="text-sm text-white truncate">{u.email}</div>
                <div className="text-xs text-gray-500 truncate mt-0.5">
                  {u.name || "—"} · <span className="font-mono">{u.user_id}</span>
                  {u.aff_id ? <> · aff <span className="font-mono">{u.aff_id}</span></> : null}
                </div>
              </button>
            ))
          )}
        </div>
      )}
      {error && (
        <div className="text-xs text-red-400 mt-1" data-testid={`${testIdPrefix}-error`}>{error}</div>
      )}
    </div>
  );
};

export default UserEmailPicker;
